const buildApiUrl = (path, params) => {
  const searchParams = new URLSearchParams(params);
  const pathJoined = ["", ...path].map((p) => encodeURIComponent(p)).join("/");
  const url = new URL(
    `${pathJoined}${params ? "?" : ""}${searchParams}`,
    process.env.REACT_APP_API_BASE,
  );
  return url.toString();
};

const buildOpts = (method, token, body, impersonateId) => {
  const opts = {
    method,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  if (impersonateId) {
    opts.headers["x-auth0-override"] = impersonateId;
  }

  if (body) {
    opts.body = JSON.stringify(body);
    opts.headers["Content-Type"] = "application/json";
  }

  return opts;
};

export const wrsCentral = (token, impersonateId) => {
  const getToken = () => token;
  const getImpersonationId = () => impersonateId;

  const getMe = async () => {
    const url = buildApiUrl(["central", "me"]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();

    if (status === "ok") return data;

    throw new Error(message);
  };

  const getPatient = async (id) => {
    const url = buildApiUrl(["central", "patients", id]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();

    if (status === "ok") return data;

    throw new Error(message);
  };

  const getIntake = async (id) => {
    const url = buildApiUrl(["central", "intake", id]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();

    if (status === "ok") return data;

    throw new Error(message);
  };

  const getComments = async (entity, ident) => {
    const url = buildApiUrl(["central", "comments", entity, ident]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();

    if (status === "ok") return data;

    throw new Error(message);
  };

  const getPatients = async () => {
    const url = buildApiUrl(["central", "patients"]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Failed to get list of patients");
  };

  const getPrescribers = async () => {
    const url = buildApiUrl(["central", "prescribers"]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Failed to get list of prescribers");
  };

  const getPrescriber = async (npi) => {
    const url = buildApiUrl(["central", "prescribers", npi]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error(`Failed to get prescriber (${npi})`);
  };

  const addPrescriber = async (submission) => {
    const payload = {
      rep_id: 1,
      npi: submission.npi,
      name_preferred: submission.preferredName,
      email: submission.prescriberEmail,
      practice_name: submission.practiceName,
      practice_address: submission.practiceAddress,
      sched_name: submission.schedulerName,
      sched_phone: submission.schedulerPhone,
      sched_email: submission.schedulerEmail,
      notes: "",
    };

    const url = buildApiUrl(["central", "prescriber"]);
    const opts = buildOpts("POST", token, payload, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();

    if (status === "ok") return data;

    if (status === "validation") throw new Error(message);

    throw new Error(`Failed to add new prescriber`);
  };

  const getInventory = async () => {
    const url = buildApiUrl(["central", "inventory"]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Failed to get list of inventory");
  };

  const getInventoryId = async (id) => {
    const url = buildApiUrl(["central", "inventory", id]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Failed to inventory");
  };

  const createTok = async (tokOpts) => {
    const url = buildApiUrl(["central", "tokens"]);
    const opts = buildOpts("POST", token, tokOpts, impersonateId);
    const resp = await fetch(url, opts);
    return resp.json();
  };

  const getTok = async (tok) => {
    const url = buildApiUrl(["central", "tokens", tok]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    if (data) return data;

    throw new Error("Invalid link");
  };

  const getService = async () => {
    const url = buildApiUrl(["central", "service"]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Failed to get service list");
  };

  const getDashboard = async () => {
    const url = buildApiUrl(["central", "dashboard"]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Unable to load dashboard");
  };

  const getTreatment = async (id) => {
    const url = buildApiUrl(["central", "treatment", id]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Failed to lookup patient treatment");
  };

  const schedulePatientFittingWithReason = async (id, date, reason) => {
    const url = buildApiUrl(["central", "service", id, "schedule_fitting"]);

    const payload = {
      date: date,
      reason: reason,
    };

    const opts = buildOpts("POST", token, payload, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Unable to schedule fitting.");
  };

  const endPatientTreatmentWithReason = async (id, date, reason) => {
    const url = buildApiUrl(["central", "service", id, "end"]);

    const payload = {
      service_end_on: date,
      reason: reason,
    };

    const opts = buildOpts("POST", token, payload, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Unable to end treatment.");
  };

  const getNpiFromRegistry = async (npi) => {
    const url = buildApiUrl(["central", "npi", "registry", npi]);

    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Failed to reach NPI registry");
  };

  const schedulePatientPickup = async (id, date) => {
    const url = buildApiUrl(["central", "service", id, "schedule_pickup"]);

    const payload = {
      date: date,
    };

    const opts = buildOpts("POST", token, payload, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Unable to schedule pickup.");
  };

  const fitPatient = async (
    id,
    sigs,
    fit_on,
    pdfData,
    inv_serial_id,
    inv_additional,
  ) => {
    const url = buildApiUrl(["central", "treatment", id, "pic"]);

    const payload = {
      sigs: sigs,
      date: fit_on,
      pdfData: pdfData,
      inv_serial_id: inv_serial_id,
      wraps: inv_additional,
    };

    const opts = buildOpts("POST", token, payload, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();

    if (status === "ok") return data;

    throw new Error(message);
  };

  const doCopa = async (treatId, sigs, picked_up_on, pdfData) => {
    const url = buildApiUrl(["central", "treatment", treatId, "copa"]);

    const payload = {
      sigs: sigs,
      picked_up_on: picked_up_on,
      pdfData: pdfData,
    };

    const opts = buildOpts("POST", token, payload, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();

    if (status === "ok") return data;

    throw new Error(message);
  };

  const enrollNpiSam = async (npi, sigs) => {
    const url = buildApiUrl(["central", "enroll", npi, "sam2"]);

    const payload = {
      sigs: sigs,
      settings: {},
    };

    const opts = buildOpts("POST", token, payload, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Unable to enroll in program");
  };

  const enrollNpiVt4 = async (npi, sigs, settings) => {
    const url = buildApiUrl(["central", "enroll", npi, "vt4"]);

    const payload = {
      sigs,
      rtm: settings.rtm,
      settings: {
        lg: {
          temp: settings.largeTemperature,
          temp_change: settings.largeAllowTempChange,
          compress: settings.largeCompression,
          compress_change: settings.largeAllowCompressionChange,
          freq: settings.largeFrequency,
        },
        sm: {
          temp: settings.smallTemperature,
          compress: settings.smallCompression,
          compress_change: settings.smallAllowCompressionChange,
          freq: settings.smallFrequency,
        },
      },
    };

    const opts = buildOpts("POST", token, payload, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Unable to enroll in program");
  };

  const enrollNpiGr = async (npi, sigs, settings) => {
    const url = buildApiUrl(["central", "enroll", npi, "gr"]);

    const payload = {
      sigs,
      settings: {
        direction_type: settings.direction_type,
        times_per_day: settings.times_per_day,
        pressure: settings.pressure,
        minutes: settings.minutes,
        program: settings.program,
        additional_instructions: settings.additional_instructions,
      },
    };

    const opts = buildOpts("POST", token, payload, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();

    if (status === "ok") return data;

    throw new Error(message);
  };

  const ezReport = async (type) => {
    const url = buildApiUrl(["central", "ez-report"]);

    const payload = {
      report: type,
    };

    const opts = buildOpts("POST", token, payload, impersonateId);
    const resp = await fetch(url, opts);

    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Unable to pull report");
  };

  const ezReportWeekly = async () => {
    const url = buildApiUrl(["central", "ez-report", "weekly"]);

    const opts = buildOpts("POST", token, undefined, impersonateId);
    const resp = await fetch(url, opts);

    const { status, data } = await resp.json();

    if (status === "ok") return data;

    throw new Error("Unable to pull report");
  };

  const itemList = async (therapy, type) => {
    let url = buildApiUrl(["central", "items"]);
    if (therapy || type) {
      const q = new URLSearchParams();
      if (therapy) q.set("therapy", therapy);
      if (type) q.set("type", type);
      url = `${url}?${q.toString()}`;
    }

    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();
    if (status === "ok") return data;
    throw new Error(message);
  };

  const uploadAttachment = async (base64Data, filename, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = buildApiUrl(["central", "attachments"]);

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener("load", () => {
        try {
          const { status, data, message } = JSON.parse(xhr.responseText);
          if (status === "ok") resolve(data);
          else reject(new Error(message || "Upload failed"));
        } catch (error) {
          reject(new Error("Invalid server response"));
        }
      });

      xhr.addEventListener("error", () =>
        reject(new Error("Upload failed. Check your connection.")),
      );
      xhr.addEventListener("abort", () =>
        reject(new Error("Upload cancelled")),
      );

      xhr.open("POST", url);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.setRequestHeader("Accept", "application/json");
      if (impersonateId) {
        xhr.setRequestHeader("x-auth0-override", impersonateId);
      }

      xhr.send(JSON.stringify({ data: base64Data, filename }));
    });
  };

  const getAttachment = async (attachmentId) => {
    const url = buildApiUrl(["central", "attachments", "meta", attachmentId]);
    const opts = buildOpts("GET", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();

    if (status === "ok") return data;
    throw new Error(message || "Failed to get attachment");
  };

  const deleteAttachment = async (attachmentId) => {
    const url = buildApiUrl(["central", "attachments", attachmentId]);
    const opts = buildOpts("DELETE", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, message } = await resp.json();

    if (status === "ok") return true;
    throw new Error(message || "Failed to delete attachment");
  };

  const viewAttachment = async (attachmentId) => {
    const url = buildApiUrl(["central", "attachments", "view", attachmentId]);
    const opts = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    if (impersonateId) {
      opts.headers["x-auth0-override"] = impersonateId;
    }

    const resp = await fetch(url, opts);
    if (!resp.ok) {
      throw new Error("Failed to load file");
    }

    const blob = await resp.blob();
    const contentType =
      resp.headers.get("content-type") || "application/octet-stream";
    return { blob, contentType };
  };

  const submitNewPatient = async (payload) => {
    const url = buildApiUrl(["central", "patients", "new"]);
    const opts = buildOpts("POST", token, payload, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();
    if (status !== "ok") throw new Error(message);
    return data;
  };

  const sendRentalAgreement = async (treatId) => {
    const url = buildApiUrl(["central", "treatment", treatId, "send-ra"]);
    const opts = buildOpts("POST", token, undefined, impersonateId);
    const resp = await fetch(url, opts);
    const { status, data, message } = await resp.json();
    if (status !== "ok") throw new Error(message);
    return data;
  };

  return {
    sendRentalAgreement,
    getComments,
    getToken,
    getImpersonationId,
    getMe,
    getService,
    getTreatment,
    getDashboard,
    getPatient,
    getIntake,
    getPatients,
    getPrescribers,
    getPrescriber,
    addPrescriber,
    getInventory,
    getInventoryId,
    getNpiFromRegistry,
    enrollNpiSam,
    enrollNpiVt4,
    enrollNpiGr,
    schedulePatientFittingWithReason,
    schedulePatientPickup,
    fitPatient,
    doCopa,
    endPatientTreatmentWithReason,
    createTok,
    getTok,
    ezReport,
    ezReportWeekly,
    itemList,
    uploadAttachment,
    getAttachment,
    deleteAttachment,
    viewAttachment,
    submitNewPatient,
  };
};
